import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { FilterBar } from '../../components/FilterBar';
import { Input } from '../../components/Input';
import { Select } from '../../components/Select';
import { listBranches } from '../../data/fetchers';
import { dashboardApi } from '../../lib/apiClient';
import { fmtAmount, fmtInt } from '../../lib/format';
import { visibleBranchIds } from '../../lib/permissions';
import { useBranchScope } from '../../lib/useBranchScope';
import { useSession } from '../../stores/session';
import { ReportHeader } from './ReportHeader';

const today = () => new Date().toISOString().slice(0, 10);

export function ZReportView() {
  const { scope, ready } = useBranchScope();
  const [date, setDate] = useState(today);
  const [downloading, setDownloading] = useState<'pdf' | 'excel' | null>(null);
  const user = useSession((state) => state.user);
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const [selectedBranch, setSelectedBranch] = useState('');
  const allowed = user
    ? visibleBranchIds(
        user,
        branches.map((branch) => branch.id),
      )
    : [];
  const visible = branches.filter((branch) => allowed.includes(branch.id));
  const requestedBranch = selectedBranch || scope.branchId;
  const branchId =
    visible.find((branch) => branch.id === requestedBranch)?.id ??
    (visible.length === 1 ? visible[0].id : undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['report', 'z-report', date, branchId, scope.allowedBranchIds],
    queryFn: () => dashboardApi.getZReport({ date, branchId }),
    enabled: ready && !!branchId,
  });

  const download = async (format: 'pdf' | 'excel') => {
    if (!branchId) return;
    setDownloading(format);
    try {
      const blob = await dashboardApi.downloadZReport({
        date,
        branchId,
        format,
      });
      downloadBlob(
        blob,
        `z-report-${date}.${format === 'pdf' ? 'pdf' : 'csv'}`,
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      <ReportHeader
        title="Z-Report"
        subtitle="รายงานปิดวัน"
        status={{
          label: downloading
            ? 'กำลังเตรียมไฟล์'
            : isLoading
              ? 'กำลังประมวลผล'
              : data
                ? `ข้อมูลวันที่ ${date}`
                : 'ยังไม่มีข้อมูล',
          tone: downloading || isLoading ? 'neutral' : data ? 'ok' : 'warn',
        }}
        actions={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
            <Btn
              kind="default"
              onClick={() => void download('pdf')}
              disabled={!branchId || !data || downloading !== null}
            >
              PDF
            </Btn>
            <Btn
              kind="default"
              onClick={() => void download('excel')}
              disabled={!branchId || !data || downloading !== null}
            >
              Excel
            </Btn>
          </div>
        }
      />

      <FilterBar>
        <label className="grid w-full gap-2 text-sm-label font-bold sm:w-auto sm:min-w-48">
          สาขา
          <Select
            aria-label="สาขา"
            value={branchId ?? ''}
            onChange={(event) => setSelectedBranch(event.target.value)}
          >
            <option value="" disabled>
              เลือกสาขา
            </option>
            {visible.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="grid w-full min-w-0 gap-2 sm:w-auto">
          <span
            id="z-report-date-heading"
            className="text-sm-label font-bold text-ink-2"
          >
            วันที่ปิดยอด
          </span>
          <Input
            aria-label="วันที่"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
        <p className="text-body text-ink-3 sm:text-right">
          ตัวเลขทั้งหมดด้านล่างอ้างอิงวันธุรกิจที่เลือก
        </p>
      </FilterBar>
      {!branchId && (
        <p role="status" className="text-warn">
          เลือกสาขาเดียวเพื่อดู Z-Report และดาวน์โหลดรายงาน
        </p>
      )}

      {(data?.sales.legacyAccountingReceiptCount ?? 0) > 0 && (
        <p className="rounded-btn bg-warn-light px-3 py-2 text-sm text-warn">
          มีข้อมูลเก่า {fmtInt(data!.sales.legacyAccountingReceiptCount)}{' '}
          บิลที่ยังไม่มีการแยกยอดก่อนส่วนลด ระบบใช้ยอดสุทธิของบิลเหล่านั้น
          และไม่รวมโปรโมชั่นเดิมซ้ำ
        </p>
      )}

      <section
        aria-labelledby="z-report-summary-heading"
        className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]"
      >
        <div className="bo-card min-w-0 overflow-hidden bg-surface p-5 text-ink sm:p-6">
          <p
            id="z-report-summary-heading"
            className="text-sm-label font-bold uppercase tracking-[0.12em] text-ink-2"
          >
            ยอดขายสุทธิ
          </p>
          <div className="mt-3 flex min-w-0 flex-wrap items-baseline gap-2">
            <strong className="num max-w-full break-words text-[clamp(1.5rem,8vw,4rem)] font-extrabold leading-none tracking-tight">
              {data ? fmtAmount(data.sales.total) : '—'}
            </strong>
            <span className="font-semibold text-ink-2">บาท</span>
          </div>
          <div className="mt-5 grid gap-4 border-t border-white/10 pt-4 min-[420px]:grid-cols-2">
            <SummaryValue
              label="กำไรขั้นต้น"
              value={data ? fmtAmount(data.sales.grossProfit) : '—'}
              inverse
            />
            <SummaryValue
              label="VAT 7%"
              value={data ? fmtAmount(data.vat.vatAmount) : '—'}
              inverse
            />
          </div>
        </div>

        <div
          className={`bo-card min-w-0 overflow-hidden border p-5 ${
            data && data.cash.discrepancy !== 0
              ? 'border-warn/30 bg-warn-light'
              : 'border-ok/20 bg-ok-light'
          }`}
        >
          <p className="text-sm-label font-bold uppercase tracking-[0.12em] text-ink-3">
            เงินสดต่าง
          </p>
          <div
            className={`mt-3 num max-w-full break-words text-[clamp(1.5rem,7vw,3.25rem)] font-extrabold leading-none ${
              data && data.cash.discrepancy !== 0 ? 'text-warn' : 'text-ok'
            }`}
          >
            {data ? fmtAmount(data.cash.discrepancy) : '—'}
          </div>
          <p className="mt-3 text-body text-ink-2">
            {data && data.cash.discrepancy !== 0
              ? 'ยอดนับเงินจริงไม่ตรงกับยอดที่ระบบคาดไว้ ควรตรวจสอบก่อนปิดวัน'
              : 'ยอดเงินสดตรงกับระบบ ไม่มีส่วนต่างที่ต้องติดตาม'}
          </p>
        </div>
      </section>

      <Card title="องค์ประกอบยอดขาย">
        <div className="grid gap-x-8 sm:grid-cols-2">
          <SummaryRow
            label="ยอดก่อนส่วนลด"
            value={data ? fmtAmount(data.sales.gross ?? data.sales.total) : '—'}
          />
          <SummaryRow
            label="มูลค่าโปรโมชั่น"
            value={data ? fmtAmount(data.sales.promotionDiscount ?? 0) : '—'}
          />
          <SummaryRow
            label="ส่วนลดท้ายบิล"
            value={data ? fmtAmount(data.sales.billDiscount ?? 0) : '—'}
          />
          <SummaryRow
            label="ยอดปัดเศษ"
            value={data ? fmtAmount(data.sales.roundingDelta ?? 0) : '—'}
          />
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card title="โปรโมชั่นที่ใช้">
          <Rows
            empty={isLoading ? 'กำลังโหลด' : 'ไม่มีโปรโมชั่น'}
            rows={(data?.promotions ?? []).map((row) => ({
              key: row.promotionId,
              label: `${row.promotionName} · ${fmtInt(row.receiptCount)} บิล${
                row.freeQuantity > 0
                  ? ` · แถม ${fmtInt(row.freeQuantity)} ชิ้น`
                  : ''
              }`,
              value: fmtAmount(row.discountAmount),
            }))}
          />
        </Card>
        <Card title="แยกตามช่องทางชำระ">
          <Rows
            empty={isLoading ? 'กำลังโหลด' : 'ไม่มีข้อมูล'}
            rows={(data?.payments ?? []).map((row) => ({
              key: row.method,
              label: row.label,
              value: fmtAmount(row.amount),
            }))}
          />
        </Card>
        <Card title="หมวดหมู่">
          <Rows
            empty={isLoading ? 'กำลังโหลด' : 'ไม่มีข้อมูล'}
            rows={(data?.categories ?? []).map((row) => ({
              key: row.categoryId,
              label: row.categoryName,
              value: fmtAmount(row.amount),
            }))}
          />
        </Card>
        <Card title="สินค้าขายดี">
          <Rows
            empty={isLoading ? 'กำลังโหลด' : 'ไม่มีข้อมูล'}
            rows={(data?.topByQty ?? []).map((row) => ({
              key: row.productId,
              label: row.productName,
              value: `${fmtInt(row.quantity)} / ${fmtAmount(row.amount)}`,
            }))}
          />
        </Card>
      </div>
    </div>
  );
}

function SummaryValue({
  label,
  value,
  inverse = false,
}: {
  label: string;
  value: string;
  inverse?: boolean;
}) {
  return (
    <div>
      <div className={`text-sm-label ${inverse ? 'text-ink-2' : 'text-ink-3'}`}>
        {label}
      </div>
      <div
        className={`mt-1 num text-h2 font-bold ${inverse ? 'text-ink' : 'text-ink'}`}
      >
        {value}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-4 border-b border-line py-3 last:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0">
      <span className="text-body text-ink-2">{label}</span>
      <strong className="num shrink-0 text-body text-ink">{value}</strong>
    </div>
  );
}

function Rows({
  rows,
  empty,
}: {
  rows: Array<{ key: string; label: string; value: string }>;
  empty: string;
}) {
  if (rows.length === 0) {
    return <div className="py-8 text-center text-body text-ink-3">{empty}</div>;
  }
  return (
    <div className="divide-y divide-line">
      {rows.map((row, index) => (
        <div
          key={`${row.key}:${index}`}
          className="flex items-center justify-between gap-4 py-3"
        >
          <span className="min-w-0 truncate text-body font-medium text-ink">
            {row.label}
          </span>
          <span className="num shrink-0 text-body font-bold text-ink">
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
